import { useMemo } from 'react';
import type { Game } from '../hooks/useGames';
import type { GameLive } from '../hooks/useGamesLive';
import { TabId } from './TabBar';
import CategoriesSection from './CategoriesSection';
import GameRow from './GameRow';
import GameGrid from './GameGrid';
import Loader from './Loader';
import { 
  convertLiveCategoriesToCategories, 
  convertLiveMenuToMenu
} from '../utils/liveGameUtils';
import type {
  CategoryWithGamesLive,
  MenuWithItems 
} from '../utils/liveGameUtils';

interface MainContentData {
  menu?: any[];
  games?: Game[];
  categories?: any[];
}

interface MainContentProps {
  data: MainContentData | null;
  selectedMenuId: string | null;
  selectedTab: TabId;
  gamesLive: GameLive[];
  loading: boolean;
  onGamePlay: (game: Game) => void;
  onGameInfo: (game: Game) => void;
  onGamePromoClick?: (promoUrl: string) => void;
  filteredGames?: Game[] | null;
}

export default function MainContent({
  data,
  selectedMenuId,
  selectedTab,
  gamesLive,
  loading,
  onGamePlay,
  onGameInfo,
  onGamePromoClick,
  filteredGames
}: MainContentProps) {
  const renderContent = useMemo(() => {
    if (!data) return null;

    const selectedMenu = data.menu?.find(menu => menu.id === selectedMenuId);
    if (!selectedMenu) return null;

    // Use filtered games if available, otherwise use all games
    if (filteredGames) {
      return (
        <GameGrid
          games={filteredGames}
          menu={selectedMenu}
          selectedTab={selectedTab}
          gamesLive={gamesLive}
          onGamePlay={onGamePlay}
          onGameInfo={onGameInfo}
          onGamePromoClick={onGamePromoClick}
        />
      );
    }

    if (data.games && Array.isArray(data.games)) {
      return (
        <GameGrid
          games={data.games}
          menu={selectedMenu}
          selectedTab={selectedTab}
          gamesLive={gamesLive}
          onGamePlay={onGamePlay}
          onGameInfo={onGameInfo}
          onGamePromoClick={onGamePromoClick}
        />
      );
    }

    if (selectedMenu.style === "categories" && data.categories && selectedMenu.items) {
      const categoriesToShow = selectedTab === TabId.CASINO_LIVE 
        ? convertLiveCategoriesToCategories(data.categories as CategoryWithGamesLive[], gamesLive)
        : data.categories;
        
      return (
        <CategoriesSection
          categories={categoriesToShow}
          categoryIds={selectedMenu.items}
          onGamePlay={onGamePlay}
          onGameInfo={onGameInfo}
          gamesLive={gamesLive}
          onGamePromoClick={onGamePromoClick}
        />
      );
    }

    if (selectedMenu.games && selectedMenu.games.length > 0) {
      return (
        <GameRow
          title={selectedMenu.name}
          games={selectedMenu.games}
          size="large"
          onGamePlay={onGamePlay}
          onGameInfo={onGameInfo}
          gamesLive={gamesLive}
          onGamePromoClick={onGamePromoClick}
        />
      );
    }

    if (selectedTab === TabId.CASINO_LIVE && selectedMenu.items && selectedMenu.items.length > 0) {
      const convertedMenu = convertLiveMenuToMenu(selectedMenu as MenuWithItems, gamesLive);
      
      if (convertedMenu.games && convertedMenu.games.length > 0) {
        return (
          <GameGrid
            games={convertedMenu.games}
            menu={selectedMenu}
            selectedTab={selectedTab}
            gamesLive={gamesLive}
            onGamePlay={onGamePlay}
            onGameInfo={onGameInfo}
            onGamePromoClick={onGamePromoClick}
          />
        );
      }
    }

    return null;
  }, [data, selectedMenuId, selectedTab, gamesLive, onGamePlay, onGameInfo, onGamePromoClick, filteredGames]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader size="medium" />
      </div>
    );
  }

  return <>{renderContent}</>;
}